import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import LogoLoader from '../components/ui/LogoLoader';
import { 
  CheckCircleIcon, 
  ClockIcon, 
  TruckIcon, 
  ExclamationTriangleIcon,
  ShoppingBagIcon,
  DocumentTextIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';

interface OrderData {
  id: number;
  order_number: string;
  status: string;
  payment_status: string;
  total_amount: number;
  created_at: string;
  orderItems?: any[];
  order_items?: any[];
}

interface OrderStatusInfo {
  current_status: string;
  payment_status: string;
  status_display: string;
  payment_status_display: string;
  can_cancel: boolean;
  estimated_delivery: string | null;
  order_timeline: Array<{
    status: string;
    date: string | null;
    completed: boolean;
    description: string;
  }>;
}

const OrderSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get('order');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [statusInfo, setStatusInfo] = useState<OrderStatusInfo | null>(null);

  useEffect(() => {
    // If no order number in URL, redirect to home
    if (!orderNumber) {
      navigate('/', { replace: true });
      return;
    }

    const fetchOrderStatus = async () => {
      try {
        setLoading(true);
        const response = await apiService.getOrderStatus(orderNumber);
        setOrderData(response.order);
        setStatusInfo(response.status_info);
      } catch (error: any) {
        // If order not found or unauthorized, redirect to orders page
        if (error.response?.status === 404 || error.response?.status === 401) {
          navigate('/orders', { replace: true });
        } else {
          setError('Failed to load order details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrderStatus();
  }, [orderNumber, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 pb-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <LogoLoader size="lg" text="Loading order details..." />
        </div>
      </div>
    );
  }

  if (error || !orderData || !statusInfo) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 pb-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-2xl p-12 shadow-sm text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
              <ExclamationTriangleIcon className="h-10 w-10 text-red-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Order Not Found</h2>
            <p className="text-gray-600 text-lg mb-8">
              {error || 'We couldn\'t find this order. It may have been removed or you don\'t have permission to view it.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/orders')}
                className="inline-flex items-center px-8 py-4 bg-black text-white rounded-lg hover:bg-gray-800 transition-all duration-200 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <DocumentTextIcon className="h-6 w-6 mr-2" />
                View My Orders
              </button>
              <button
                onClick={() => navigate('/product-signature')}
                className="inline-flex items-center px-8 py-4 bg-white text-gray-900 border-2 border-gray-900 rounded-lg hover:bg-gray-50 transition-all duration-200 font-semibold text-lg"
              >
                <ShoppingBagIcon className="h-6 w-6 mr-2" />
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isPaymentPending = orderData.payment_status === 'pending_approval';
  const isCOD = orderData.payment_status === 'pending';
  
  // Get items count - handle different possible property names
  const itemsCount = orderData.orderItems?.length || 
                     orderData.order_items?.length || 
                     (orderData as any).items?.length || 
                     0;
  
  // Get status color based on order status
  const getOrderStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'shipped':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-20 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Success Header - Hero Section */}
        <div className="text-center mb-6 sm:mb-8 animate-slideUp">
          <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full mb-4 sm:mb-6 shadow-2xl animate-bounce">
            <CheckCircleIcon className="h-12 w-12 sm:h-14 sm:w-14 text-white" strokeWidth={2.5} />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-3 px-4">
            Order Confirmed!
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-4 sm:mb-6 px-4">
            Thank you for shopping with us
          </p>
          
          {/* Order Number Badge */}
          <div className="inline-block bg-white rounded-xl px-6 py-3 sm:px-8 sm:py-4 shadow-lg border-2 border-gray-100 mx-4">
            <p className="text-xs sm:text-sm text-gray-500 uppercase tracking-wide font-semibold mb-1">Order Number</p>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight break-all">{orderData.order_number}</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
          
          {/* Left Column - Order Details */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            
            {/* Order Summary Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                  <DocumentTextIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-gray-700" />
                  Order Summary
                </h2>
              </div>
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500 mb-1">Total Amount</p>
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                      {orderData.total_amount.toLocaleString("en-US")} <span className="text-lg sm:text-xl">Ks</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs sm:text-sm text-gray-500 mb-1">Items</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{itemsCount}</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-100 space-y-3">
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs sm:text-sm font-semibold text-gray-700">Order Status:</p>
                      <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-semibold border ${getOrderStatusColor(orderData.status)}`}>
                        {statusInfo.status_display}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs sm:text-sm font-semibold text-gray-700">Payment Status:</p>
                      <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-semibold border ${
                        orderData.payment_status === 'pending_approval' ? 'bg-amber-100 text-amber-800 border-amber-200' :
                        orderData.payment_status === 'pending' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                        orderData.payment_status === 'approved' || orderData.payment_status === 'paid' ? 'bg-green-100 text-green-800 border-green-200' :
                        orderData.payment_status === 'rejected' ? 'bg-red-100 text-red-800 border-red-200' :
                        'bg-gray-100 text-gray-800 border-gray-200'
                      }`}>
                        {statusInfo.payment_status_display}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600">
                    📅 Placed on {new Date(orderData.created_at).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Status Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {isPaymentPending ? (
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-4 sm:p-6 border-l-4 border-amber-400">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 rounded-xl">
                        <ClockIcon className="h-6 w-6 sm:h-7 sm:w-7 text-amber-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-bold text-amber-900 mb-2">
                        Payment Under Review
                      </h3>
                      <p className="text-sm sm:text-base text-amber-800 leading-relaxed mb-4">
                        We're verifying your payment. This usually takes 2-4 hours. You'll receive an email once approved.
                      </p>
                      <div className="bg-white bg-opacity-60 rounded-lg p-3 sm:p-4 border border-amber-200">
                        <p className="text-xs sm:text-sm font-semibold text-amber-900 mb-2">📋 Next Steps:</p>
                        <ul className="text-xs sm:text-sm text-amber-800 space-y-1.5">
                          <li className="flex items-start">
                            <span className="mr-2 flex-shrink-0">✓</span>
                            <span>Payment verification in progress</span>
                          </li>
                          <li className="flex items-start">
                            <span className="mr-2 flex-shrink-0">✓</span>
                            <span>Email confirmation after approval</span>
                          </li>
                          <li className="flex items-start">
                            <span className="mr-2 flex-shrink-0">✓</span>
                            <span>Order processing & shipping</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ) : isCOD ? (
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 sm:p-6 border-l-4 border-blue-400">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-xl">
                        <TruckIcon className="h-6 w-6 sm:h-7 sm:w-7 text-blue-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-bold text-blue-900 mb-2">
                        Cash on Delivery
                      </h3>
                      <p className="text-sm sm:text-base text-blue-800 leading-relaxed mb-4">
                        Your order is confirmed! Please have the exact amount ready when our delivery person arrives.
                      </p>
                      <div className="bg-white bg-opacity-60 rounded-lg p-3 sm:p-4 border border-blue-200">
                        <p className="text-xs sm:text-sm font-semibold text-blue-900 mb-2">🚚 Delivery Info:</p>
                        <p className="text-xs sm:text-sm text-blue-800">
                          Expected delivery: <strong>3-7 business days</strong>
                          <br />
                          You'll receive a call before delivery.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 sm:p-6 border-l-4 border-green-400">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-xl">
                        <CheckCircleIcon className="h-6 w-6 sm:h-7 sm:w-7 text-green-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-bold text-green-900 mb-2">
                        Payment Confirmed
                      </h3>
                      <p className="text-sm sm:text-base text-green-800 leading-relaxed">
                        Your payment has been verified. We're preparing your order for shipment!
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Actions */}
          <div className="lg:col-span-1 space-y-4 sm:space-y-6">
            
            {/* Action Buttons */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">What's Next?</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/product-signature')}
                  className="w-full flex items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-black text-white rounded-xl hover:bg-gray-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:scale-105 text-sm sm:text-base"
                >
                  <ShoppingBagIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Continue Shopping
                </button>
                <button
                  onClick={() => navigate('/orders')}
                  className="w-full flex items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-white text-gray-900 border-2 border-gray-900 rounded-xl hover:bg-gray-50 transition-all duration-200 font-semibold text-sm sm:text-base"
                >
                  <DocumentTextIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  View Order
                </button>
              </div>
            </div>

            {/* Contact Support */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full mb-2 sm:mb-3 shadow-sm">
                  <PhoneIcon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-700" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-1 sm:mb-2">Need Help?</h3>
                <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                  Our customer service team is here to assist you
                </p>
                <a 
                  href="tel:+959943000730"
                  className="inline-flex items-center text-sm sm:text-base text-gray-900 font-semibold hover:text-gray-700 transition-colors"
                >
                  <PhoneIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  +95 (9943) 000730
                </a>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center text-xs sm:text-sm text-gray-600">
                  <svg className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Secure Payment
                </div>
                <div className="flex items-center text-xs sm:text-sm text-gray-600">
                  <svg className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 text-blue-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                  </svg>
                  Fast Delivery
                </div>
                <div className="flex items-center text-xs sm:text-sm text-gray-600">
                  <svg className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 text-purple-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Quality Guaranteed
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
